/**
 * Calculation model for the tyre rack ROI calculator.
 *
 * Geometry (rack capacity, footprint, stories) is fixed here. The commercial parameters (ROI horizon,
 * revenue per wheel set, rack prices) are `CalculatorParameters`: sales edits them in the admin backend,
 * the component fetches them from the API, and every stored lead keeps the set it was computed with.
 * Bump CALCULATION_MODEL_VERSION whenever the geometry or a formula changes so stored leads stay
 * interpretable.
 */

export const CALCULATION_MODEL_VERSION = "ws3000-v3";

/** Commercial inputs of the model. Edited by sales, see the admin backend. */
export interface CalculatorParameters {
  /** Horizon of the return figures in years. */
  roiYears: number;
  /** Average revenue per stored wheel set and season, net, in EUR. */
  revenuePerWheelset: number;
  /** Net price of the first rack in a row, in EUR. */
  priceBaseRack: number;
  /** Net price of every further rack in the row, in EUR. */
  priceAddOnRack: number;
}

/**
 * Used until the API delivers the saved set, and by the API when nothing was saved yet. Prices from the
 * original prototype; three years because the customer wants a horizon that reads as realistic for small
 * and mid-sized workshops (five years produced four-digit percentages).
 */
export const DEFAULT_PARAMETERS: Readonly<CalculatorParameters> = {
  roiYears: 3,
  revenuePerWheelset: 45,
  priceBaseRack: 147.58,
  priceAddOnRack: 98.81,
};

/** Sanity bounds for saved parameters; the admin form and the schema both enforce them. */
export const PARAMETER_BOUNDS = {
  roiYears: { min: 1, max: 10 },
  revenuePerWheelset: { min: 1, max: 1000 },
  priceBaseRack: { min: 1, max: 100_000 },
  priceAddOnRack: { min: 1, max: 100_000 },
} as const satisfies Record<keyof CalculatorParameters, { min: number; max: number }>;

export const PARAMETER_KEYS = Object.keys(PARAMETER_BOUNDS) as Array<keyof CalculatorParameters>;

/** True when the value is a finite number within the bounds of that parameter. */
export function isValidParameter(key: keyof CalculatorParameters, value: unknown): value is number {
  const bounds = PARAMETER_BOUNDS[key];
  return typeof value === "number" && Number.isFinite(value) && value >= bounds.min && value <= bounds.max;
}

/** Accepts anything the API or a form may deliver and returns a complete, in-bounds set, or null. */
export function parseParameters(value: unknown): CalculatorParameters | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const out = {} as CalculatorParameters;
  for (const key of PARAMETER_KEYS) {
    const v = record[key];
    if (!isValidParameter(key, v)) return null;
    out[key] = key === "roiYears" ? Math.round(v) : Math.round(v * 100) / 100;
  }
  return out;
}

export const WHEELSET_MIN = 0;
export const FLOOR_AREA_MIN = 10;

/**
 * The calculator stops at 500 m². Larger projects need individual planning and are asked to contact
 * sales directly; the result is then flagged as a capped estimate (v2, v1 stopped at 5000 wheel sets).
 */
export const FLOOR_AREA_MAX = 500;

/** WS 3000 base rack: 2.0 x 0.9 x 0.4 m, 3 levels, about 3 wheel sets per rack. */
export const CAPACITY_PER_RACK = 3;

/** Floor area per rack position including a share of the aisle, in m². */
export const FOOTPRINT_BASE = 1.7;

/** Stacked base racks per hall height. */
export const STORIES = { low: 1, mid: 2, high: 3 } as const;
export type HallHeight = keyof typeof STORIES;
export const HALL_HEIGHTS = Object.keys(STORIES) as HallHeight[];

const MAX_STORIES = Math.max(...Object.values(STORIES));

/** Wheel sets that fit on FLOOR_AREA_MAX with the tallest racks; the absolute upper bound of the slider. */
export const WHEELSET_MAX = Math.floor(FLOOR_AREA_MAX / FOOTPRINT_BASE) * MAX_STORIES * CAPACITY_PER_RACK;

export const SEASONS_PER_YEAR = 2;

export type LastChanged = "wheelsets" | "floorArea";

export interface CalculatorInput {
  wheelsets: number;
  floorArea: number;
  height: HallHeight;
  /** Which slider the user touched last. Decides which value drives the other. */
  lastChanged: LastChanged;
}

export interface CalculationResult {
  modelVersion: string;
  rackCount: number;
  stories: number;
  floorArea: number;
  revenuePerSeason: number;
  investment: number;
  paybackSeasons: number;
  /** Horizon the two return figures refer to; copied from the parameters. */
  roiYears: number;
  /** Revenue over roiYears minus the investment, in EUR. */
  absoluteReturn: number;
  /** absoluteReturn relative to the investment, in percent. */
  roiPercent: number;
  /** True when a slider sits at its maximum (500 m²), so all values are minimums and sales should plan individually. */
  isCappedEstimate: boolean;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function isHallHeight(value: unknown): value is HallHeight {
  return typeof value === "string" && Object.hasOwn(STORIES, value);
}

export function rackCountFor(wheelsets: number): number {
  return Math.ceil(wheelsets / CAPACITY_PER_RACK);
}

export function floorAreaFromWheelsets(wheelsets: number, stories: number): number {
  const floorPositions = Math.ceil(rackCountFor(wheelsets) / stories);
  return Math.round(floorPositions * FOOTPRINT_BASE);
}

export function wheelsetsFromFloorArea(floorArea: number, stories: number): number {
  const floorPositions = Math.max(1, Math.floor(floorArea / FOOTPRINT_BASE));
  const wheelsets = floorPositions * stories * CAPACITY_PER_RACK;
  return clamp(Math.floor(wheelsets), WHEELSET_MIN, WHEELSET_MAX);
}

/** Largest floor area the slider allows. Same for every hall height; kept as a function for callers. */
export function floorAreaMaxForHeight(_height: HallHeight): number {
  return FLOOR_AREA_MAX;
}

/** Largest wheel set count the slider allows for a hall height: what fits on FLOOR_AREA_MAX. */
export function wheelsetMaxForHeight(height: HallHeight): number {
  return wheelsetsFromFloorArea(FLOOR_AREA_MAX, STORIES[height]);
}

/** Recomputes floor area after the wheel set slider or hall height changed. */
export function syncFloorAreaFromWheelsets(input: CalculatorInput): CalculatorInput {
  const wheelsets = clamp(Math.round(input.wheelsets), WHEELSET_MIN, wheelsetMaxForHeight(input.height));
  const floorArea = clamp(
    floorAreaFromWheelsets(wheelsets, STORIES[input.height]),
    FLOOR_AREA_MIN,
    floorAreaMaxForHeight(input.height),
  );
  return { ...input, wheelsets, floorArea, lastChanged: "wheelsets" };
}

/** Recomputes wheel sets after the floor area slider or hall height changed. */
export function syncWheelsetsFromFloorArea(input: CalculatorInput): CalculatorInput {
  const floorArea = clamp(Math.round(input.floorArea), FLOOR_AREA_MIN, floorAreaMaxForHeight(input.height));
  const wheelsets = wheelsetsFromFloorArea(floorArea, STORIES[input.height]);
  return { ...input, wheelsets, floorArea, lastChanged: "floorArea" };
}

/** Applies whichever sync the last user interaction requires. */
export function normalizeInput(input: CalculatorInput): CalculatorInput {
  return input.lastChanged === "floorArea" ? syncWheelsetsFromFloorArea(input) : syncFloorAreaFromWheelsets(input);
}

export function calculate(input: CalculatorInput, params: CalculatorParameters = DEFAULT_PARAMETERS): CalculationResult {
  const wheelsetMax = wheelsetMaxForHeight(input.height);
  const wheelsets = clamp(Math.round(input.wheelsets), WHEELSET_MIN, wheelsetMax);
  const stories = STORIES[input.height];
  const rackCount = rackCountFor(wheelsets);
  const floorPositions = Math.ceil(rackCount / stories);

  const floorArea =
    input.lastChanged === "floorArea"
      ? clamp(Math.round(input.floorArea), FLOOR_AREA_MIN, floorAreaMaxForHeight(input.height))
      : Math.round(floorPositions * FOOTPRINT_BASE);

  const revenuePerSeason = round2(wheelsets * params.revenuePerWheelset);
  const investment = rackCount > 0 ? round2(params.priceBaseRack + (rackCount - 1) * params.priceAddOnRack) : 0;
  const paybackSeasons = revenuePerSeason > 0 ? round2(investment / revenuePerSeason) : 0;
  const absoluteReturn = round2(params.roiYears * SEASONS_PER_YEAR * revenuePerSeason - investment);
  const roiPercent = investment > 0 ? round2((absoluteReturn / investment) * 100) : 0;

  return {
    modelVersion: CALCULATION_MODEL_VERSION,
    rackCount,
    stories,
    floorArea,
    revenuePerSeason,
    investment,
    paybackSeasons,
    roiYears: params.roiYears,
    absoluteReturn,
    roiPercent,
    isCappedEstimate: wheelsets >= wheelsetMax || floorArea >= FLOOR_AREA_MAX,
  };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
